import React from 'react';
import { PropTypes } from 'prop-types';
import { MdCheckCircleOutline, MdOutlineCancel, MdHelpOutline } from 'react-icons/md';
import Icon from '@mdi/react';
import imagePlaceholder from './imagePlaceholder.png';
import { formatNumericDate } from '../../utils/dates';
import tailwindTheme from '../../../../../config/tailwind.config';
import { iconPaths } from '../../constants/iconsList';
import HeaderIconTooltip from '../HeaderIconTooltip/HeaderIconTooltip';

const Site = (
  {
    site, setAllTagSites = () => { }, allTagSites = {}, selectAllRef = {}, sourcePage = '', setTagSitesBtnDisabled,
  },
) => {
  const {
    // eslint-disable-next-line camelcase
    site_images: siteImages = [],
    // To do, update icons for sites.
    icon,
    name,
    county,
    h2a,
    description,
    arrival,
    departure,
    priority,
  } = site;

  const siteImg = siteImages.length ? siteImages[0].image.url : imagePlaceholder;

  const handleSelectSite = (e) => {
    if (e.target.checked) {
      setTagSitesBtnDisabled(false);
      setAllTagSites({ ...allTagSites, [site.id]: true });
    } else {
      setAllTagSites({ ...allTagSites, [site.id]: false });
    }
    const allTagSitesTemp = { ...allTagSites, [site.id]: e.target.checked };
    const isAllSelected = Object.values(allTagSitesTemp).every(value => value === true);
    const isSomeSelected = Object.values(allTagSitesTemp).some(value => value === true);

    /* eslint-disable no-param-reassign */
    if (isAllSelected) {
      selectAllRef.current.indeterminate = false;
      selectAllRef.current.checked = true;
      selectAllRef.current.style.appearance = 'auto';
    } else if (isSomeSelected) {
      selectAllRef.current.indeterminate = true;
      selectAllRef.current.checked = false;
      selectAllRef.current.style.appearance = 'auto';
    } else {
      selectAllRef.current.indeterminate = false;
      selectAllRef.current.checked = false;
      selectAllRef.current.style.appearance = 'none';
      setTagSitesBtnDisabled(true);
    }
    /* eslint-enable no-param-reassign */
  };

  const priorityColor = () => {
    if (priority === 'high') {
      return 'vamos-red';
    }
    if (priority === 'medium') {
      return 'priority-medium';
    }
    if (priority === 'low') {
      return 'vamos-green1';
    }
    return 'vamos-gray';
  };

  return (
    <a data-testid={`site-card-${site.id}`} className={`relative max-w-74 bg-vamos-v-light-gray ${sourcePage !== 'tagShow' && 'h-109'} rounded-10px overflow-visible shadow-vamos-drop-shadow border-2 hover:no-underline hover:border-vamos-black`} href={`/sites/${site.id}`}>
      {/* Icon place holder */}
      <div className={`flex items-center justify-center text-vamos-white w-9 h-9 absolute mt-3.25 ml-3.5 bg-${priorityColor()} rounded-full`}>
        <Icon data-testid={icon ? undefined : 'icon-default'} className="material-symbols-outlined" path={iconPaths[icon] || iconPaths.default} size={0.8} />
      </div>
      <div className="h-42.75 flex justify-center">
        <img src={siteImg} alt="primary site img" className={siteImages.length && 'w-full object-cover rounded-t-lg'} />
      </div>
      <div className="flex flex-col justify-between mt-4 mx-6 mb-6">
        <div>
          <div className="text-lg font-bold line-clamp-2 text-left h-14">{name}</div>
          <div className="flex justify-between items-start">
            <div className="text-base">{county}</div>
            <div className="flex">
              {(() => {
                if (h2a === null) {
                  return <MdHelpOutline size={20} color={tailwindTheme.theme.extend.colors['priority-medium']} />;
                }
                if (h2a) {
                  return <MdCheckCircleOutline size={20} color={tailwindTheme.theme.extend.colors['vamos-green1']} />;
                }
                return <MdOutlineCancel size={20} color={tailwindTheme.theme.extend.colors['vamos-red']} />;
              })()}
              <div className="text-base ml-2 mb-0">
                H2A
              </div>
            </div>
          </div>
          <div className="line-clamp-1 text-left mt-2" title={site.address1}>{site.address1}</div>

          {/* Description with Tooltip */}
          <div className="relative mb-3.75 mt-2 min-h-[50px]">
            <HeaderIconTooltip
              place="top"
              spanTextStart={description}
              paddingBottom="0"
              className="p-0 text-left"
              margins="mb-0 my-auto"
              border="border-0"
              tooltipId={`description-card-${site.id}`}
              tooltipClassName="custom-tooltip tooltip-site-page"
            />
            {/* Text that triggers the tooltip */}
            <p
              className="line-clamp-1 text-left"
              data-tooltip-id={`description-card-${site.id}`}
            >
              {description}
            </p>
          </div>
        </div>

        <div>
          <div className="flex justify-between">
            <div className="text-base text-vamos-gray">ARRIVAL</div>
            <div className="text-base">{arrival ? formatNumericDate(arrival) : 'Unknown'}</div>
          </div>
          <div className="flex justify-between">
            <div className="text-base text-vamos-gray">DEPARTURE</div>
            <div className="text-base">{departure ? formatNumericDate(departure) : 'Unknown'}</div>
          </div>
        </div>
      </div>
      {sourcePage === 'tagShow' && (
        <div className="border-t-2 border-t-vamos-light-gray h-[56px] rounded-b-xl">
          <input
            type="checkbox"
            className={`${(allTagSites[site.id]) ? 'appearance-auto' : 'appearance-none'} w-[22px] h-[22px] mt-[17px] ml-[24px] rounded-[5px] border border-vamos-gray bg-vamos-v-light-gray`}
            onChange={handleSelectSite}
            checked={((allTagSites[site.id] && true) || false)}
          />
        </div>
      )}
    </a>
  );
};

export default Site;

Site.propTypes = {
  site: PropTypes.object.isRequired,
  setAllTagSites: PropTypes.func,
  allTagSites: PropTypes.object,
  selectAllRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.instanceOf(Element) })]),
  sourcePage: PropTypes.string,
  setTagSitesBtnDisabled: PropTypes.func.isRequired,
};
